import React, { useCallback, useMemo, useState } from 'react';
import { ResponsiveGrid } from '@alifd/next';
import PageHeader from '@/components/PageHeader';
import {
  Button,
  Card,
  Cascader,
  DatePicker,
  Modal,
  Select,
  Space,
  Table,
  message,
} from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import useService from '@/lib/hooks/useService';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { generateHouseHoldNumberCascader } from '@/utils/generateHouseHoldNumberCascader';
import styles from './index.module.scss';
import type { ColumnsType } from 'antd/es/table';
import type Message from '@/types/database/message';
import type { ResidentAccountNumber } from '@/types/database/residentAccountNumbers';
import type { MessagesTableDataType } from './types';
import type { SingleValueType } from 'rc-cascader/lib/Cascader';
import type { User } from '@/types/database/users';
import type { Moment } from 'moment';

dayjs.extend(advancedFormat);

const { Cell } = ResponsiveGrid;

const selectOptions = [
  {
    label: '未讀',
    value: '未讀',
  },
  {
    label: '已讀',
    value: '已讀',
  },
];

const { confirm } = Modal;

const MessageList = () => {
  const [messages, updateMessages, messageService] = useService<Message>({
    collectionName: 'message',
    query: {
      title: '個人通知',
    },
  });
  const [residents] = useService<ResidentAccountNumber>({
    collectionName: 'residentAccountNumbers',
  });
  const [users] = useService<User>({ collectionName: 'users' });

  const [selectedHouseholdNumber, setSelectedHouseholdNumber] =
    useState<SingleValueType>([]);
  const [selectedReadStatus, setSelectedReadStatus] = useState<
    '未讀' | '已讀' | null
  >(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const getUser = useCallback(
    (userId: string) => {
      return users.find((user) => {
        return user._id === userId;
      });
    },
    [users]
  );

  const getMessagesByFilterHouseholdNumber = useCallback(
    (messages: Message[], householdNumber: SingleValueType) => {
      if (householdNumber.length !== 0) {
        return messages.filter((message) => {
          const foundUser = getUser(message.userId);
          return (
            foundUser?.householdNumbers &&
            foundUser.householdNumbers[0] === householdNumber.join('-')
          );
        });
      } else {
        return messages;
      }
    },
    [getUser]
  );

  const getMessagesByFilterReadStatus = (
    messages: Message[],
    readStatus: string
  ) => {
    return messages.filter((message) => {
      if (readStatus === '未讀') {
        return message.readed === false;
      } else if (readStatus === '已讀') {
        return message.readed === true;
      } else {
        return false;
      }
    });
  };

  const messagesForDisplay = useMemo(() => {
    let filteredMessages = messages;

    if (selectedHouseholdNumber.length > 0) {
      filteredMessages = getMessagesByFilterHouseholdNumber(
        filteredMessages,
        selectedHouseholdNumber
      );
    }

    if (selectedReadStatus !== null) {
      filteredMessages = getMessagesByFilterReadStatus(
        filteredMessages,
        selectedReadStatus
      );
    }

    if (selectedDate !== null) {
      filteredMessages = filteredMessages.filter((message) => {
        return dayjs(message.createdTime).isSame(selectedDate, 'day');
      });
    }
    return filteredMessages;
  }, [
    getMessagesByFilterHouseholdNumber,
    messages,
    selectedDate,
    selectedHouseholdNumber,
    selectedReadStatus,
  ]);

  const deleteMessage = (id: string | undefined) => {
    confirm({
      title: '確定刪除此訊息?',
      icon: <ExclamationCircleOutlined />,
      content: '注意！訊息刪除後將無法復原',
      okText: '確定',
      cancelText: '取消',
      async onOk() {
        await messageService.remove(id);
        updateMessages();
        message.success('成功刪除訊息!');
      },
    });
  };

  const tableColumns: ColumnsType<MessagesTableDataType> = [
    {
      title: '訊息時間(年/月/日)',
      dataIndex: 'createdTime',
      defaultSortOrder: 'descend',
      sorter: (a, b) =>
        Number(dayjs(a.createdTime).format('x')) -
        Number(dayjs(b.createdTime).format('x')),
    },
    {
      title: '戶號',
      dataIndex: 'householdNumber',
    },
    {
      title: '住戶',
      dataIndex: 'residentName',
    },
    {
      title: '內容',
      dataIndex: 'content',
    },
    {
      title: '狀態',
      dataIndex: 'readed',
    },
    {
      title: '操作',
      dataIndex: 'action',
      fixed: 'right',
      render: (_, record) => (
        <Button type="primary" danger onClick={() => deleteMessage(record.key)}>
          刪除
        </Button>
      ),
    },
  ];

  const getHouseholdNumbersByResidents = () => {
    return residents.map((resident) => resident.householdNumber);
  };

  const filterHouseholdNumberByMessages = (messages: Message[]) => {
    const householdNumbers = getHouseholdNumbersByResidents();
    return householdNumbers.reduce<string[]>(
      (filteredHouseholdNumbers, householdNumber) => {
        messages.forEach((message) => {
          const foundUser = getUser(message.userId);
          if (
            foundUser?.householdNumbers &&
            householdNumber === foundUser?.householdNumbers[0]
          ) {
            filteredHouseholdNumbers.push(householdNumber);
          }
        });
        return filteredHouseholdNumbers;
      },
      []
    );
  };

  const getHouseholdNumberCascaderOptions = (messages: Message[]) => {
    return generateHouseHoldNumberCascader(
      filterHouseholdNumberByMessages(messages)
    );
  };

  const getMessagesTableDataSource = (messages: Message[]) => {
    return messages.reduce(
      (messagesTableDataSource: MessagesTableDataType[], message) => {
        const { _id, content, userId, readed, createdTime } = message;

        const foundUser = getUser(userId);

        if (
          foundUser?.householdNumbers !== undefined &&
          foundUser.householdNumbers.length > 0
        ) {
          messagesTableDataSource.push({
            key: _id,
            createdTime: dayjs(createdTime).format('YYYY/MM/DD HH:mm:ss'),
            householdNumber: foundUser.householdNumbers[0],
            residentName: foundUser.name,
            content,
            readed: readed ? '已讀' : '未讀',
          });
        }
        return messagesTableDataSource;
      },
      []
    );
  };

  const handleCascaderChange = (value: SingleValueType) => {
    if (value === undefined) {
      setSelectedHouseholdNumber([]);
    } else {
      setSelectedHouseholdNumber(value);
    }
  };

  const handleDateChange = (date: Moment | null) => {
    setSelectedDate(date ? date.format('YYYY/MM/DD') : date);
  };

  return (
    <ResponsiveGrid gap={20}>
      <Cell colSpan={12}>
        <PageHeader
          title="社區訊息列表"
          breadcrumbs={[
            {
              name: '社區管理',
            },
            {
              name: '社區訊息列表',
            },
          ]}
        />
      </Cell>

      <Cell colSpan={12}>
        <Space direction="vertical" className={styles.contentContainer}>
          <Space>
            <span>戶號:</span>
            <Cascader
              options={getHouseholdNumberCascaderOptions(messagesForDisplay)}
              placeholder="請選擇戶號"
              onChange={handleCascaderChange}
            />
            <span>狀態:</span>
            <Select
              options={selectOptions}
              placeholder="請選擇訊息讀取狀態"
              allowClear
              onSelect={(value: '未讀' | '已讀' | null) =>
                setSelectedReadStatus(value)
              }
              onClear={() => setSelectedReadStatus(null)}
            />
            <span>日期:</span>
            <DatePicker
              onChange={handleDateChange}
              inputReadOnly
              placeholder="請選擇訊息日期"
              className={styles.datePicker}
            />
          </Space>
          <Card bordered={false}>
            <Table
              columns={tableColumns}
              dataSource={getMessagesTableDataSource(messagesForDisplay)}
              scroll={{ x: 1100 }}
            />
          </Card>
        </Space>
      </Cell>
    </ResponsiveGrid>
  );
};

export default MessageList;
